import { Box, Stack, Typography } from '@mui/material';
import { Graph } from '../dashboardTypes';
import { useTranslation } from 'react-i18next';
import type { ChartSeriesLabelOptions } from '../../Graphs/chartSeriesLabel';
import { chartSeriesLabel, chartSeriesUsesPlainLabel } from '../../Graphs/chartSeriesLabel';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage';

interface LiveNumberWidgetProps {
  graph: Graph | undefined;
  chartSeriesLabelOptions?: ChartSeriesLabelOptions;
  /** Decimal places for the live value; defaults to 2. Use 0 for whole numbers. */
  decimalPlaces?: number;
}

const LiveNumberWidget = ({ graph, chartSeriesLabelOptions, decimalPlaces = 2 }: LiveNumberWidgetProps) => {
  const { t } = useTranslation();
  const currentData = graph?.data?.at(-1);

  const formatValue = (value: unknown): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    const num = value;
    if (decimalPlaces <= 0) return Math.round(num);
    const factor = 10 ** decimalPlaces;
    return Math.round((num + Number.EPSILON) * factor) / factor;
  };

  return (
    <Stack className="widget-body" spacing={2} justifyContent="center" alignItems="center" height="100%">
      {(graph?.series?.length ?? 0) > 0 ? (
        graph?.series?.map((series) => {
          const seriesName = chartSeriesLabel(t, series.dataKey, chartSeriesLabelOptions);
          const plainSeriesName = chartSeriesUsesPlainLabel(series.dataKey, chartSeriesLabelOptions);
          return (
            <Stack
              key={series.dataKey}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                backgroundColor: series.color,
                border: 0,
                borderRadius: 3,
                minWidth: 100,
                p: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {plainSeriesName
                    ? `${seriesName}: `
                    : t('liveNumberWidget.seriesLine', {
                        defaultValue: '{{name}}: ',
                        name: seriesName,
                      })}
                </Typography>
              </Box>
              <Typography variant="sectionTitle">{currentData ? formatValue(currentData[series.dataKey]) ?? '' : ''}</Typography>
            </Stack>
          );
        })
      ) : (
        <GraphNoDataMessage />
      )}
    </Stack>
  );
};

export default LiveNumberWidget;
